import type { ArtifactV1Data } from "@dd/schema";
import { createStore } from "zustand/vanilla";
import { getApp } from "../apps/registry";
import { playSfx } from "../audio/sound";
import { useGameStore } from "../state/gameStore";
import { windowStore } from "../wm/useWindowStore";
import { now } from "./clock";

/**
 * Navegación cruzada de artefactos (D-17): cualquier app puede abrir un
 * artefacto en SU app dueña (adjunto de Mail → Visor; tarjeta del Corcho →
 * app de origen). Mantiene la selección por app; el render del detalle
 * emite artifactViewed (lo hace cada app al mostrar la selección).
 */

export interface ArtifactNavState {
  /** appId → artifactId seleccionado */
  selected: Record<string, string | null>;
  select: (appId: string, artifactId: string | null) => void;
}

export const artifactNav = createStore<ArtifactNavState>()((set) => ({
  selected: {},
  select: (appId, artifactId) => set((s) => ({ selected: { ...s.selected, [appId]: artifactId } })),
}));

/** Abre la app dueña del artefacto, lo selecciona y enfoca la ventana. */
export function openArtifact(artifact: ArtifactV1Data): void {
  const app = getApp(artifact.app);
  if (!app) return;
  const vp = { vw: window.innerWidth, vh: window.innerHeight };
  windowStore.getState().open(app.id, vp, app.defaultSize);
  artifactNav.getState().select(app.id, artifact.id);
}

/** Fija un artefacto al corcho (botón o Ctrl+P) — evento de dominio + sello de goma. */
export function pinArtifact(artifactId: string): void {
  const alreadyPinned =
    useGameStore.getState().derived?.pinnedEvidence.includes(artifactId) ?? false;
  useGameStore.getState().append({ t: "evidencePinned", id: artifactId, at: now() });
  if (!alreadyPinned) playSfx("pin");
}

/** Marca un artefacto como visto — lo llaman las apps al render del detalle. */
export function viewArtifact(artifactId: string): void {
  const derived = useGameStore.getState().derived;
  // idempotente a nivel evento: el reduce ya des-duplica, pero evitamos
  // inflar el log si ya está visto (telemetría más legible)
  if (derived?.viewedArtifacts.has(artifactId)) return;
  useGameStore.getState().append({ t: "artifactViewed", id: artifactId, at: now() });
}
